import { useMemo } from "react";
import { C } from "../lib/theme.js";
import { fmt } from "../lib/compute.js";

// CapEx YTD progress. Full-width bar anchored at the estimated annual
// budget; fill = actual spent, tick marker = expected by now. Pace badge
// reads "ratio of actual to YTD-budget" so the owner can see at a glance
// whether we're keeping up with planned capital outlay.
export default function CapExProgress({ properties, isOwner, getPct, period, demo = false }) {
  const { spent, expected, annual, pace, monthsElapsed } = useMemo(() => {
    let spent = 0;
    let expected = 0;
    for (const p of properties || []) {
      const pct = isOwner ? getPct(p) : 1;
      if (isOwner && !pct) continue;
      spent += (p.capex?.ytd_a || 0) * pct;
      expected += (p.capex?.ytd_b || 0) * pct;
    }
    const m = /^\d{4}-\d{2}$/.test(period || "") ? parseInt(period.slice(5, 7), 10) : 0;
    const annual = m > 0 && expected > 0 ? (expected * 12) / m : 0;
    const pace = expected > 0 ? spent / expected : 0;
    return {
      spent: Math.round(spent),
      expected: Math.round(expected),
      annual: Math.round(annual),
      pace,
      monthsElapsed: m,
    };
  }, [properties, isOwner, getPct, period]);

  // Pace color (no textual label — the dollar gap + color is enough signal).
  const badge = (() => {
    if (expected <= 0) return null;
    if (pace > 1.1) return { color: C.blue, bg: C.blueBg };
    if (pace >= 0.95) return { color: C.green, bg: C.greenBg };
    if (pace >= 0.7) return { color: C.amber, bg: C.amberBg };
    return { color: C.red, bg: C.redBg };
  })();

  // Dollar delta between YTD budget and YTD actual, rounded to the nearest
  // $1K (so "$548.8K behind" reads "$549K behind"). Positive = under budget
  // (behind on spend); negative = over budget (ahead on spend). Suppressed
  // when the rounded figure is zero — the bar already tells that story.
  const gap = expected - spent;
  const absGap = Math.abs(gap);
  const gapLabel = (() => {
    if (absGap >= 1e6) {
      return `$${(absGap / 1e6).toFixed(1)}M ${gap > 0 ? "behind" : "ahead"}`;
    }
    const k = Math.round(absGap / 1000);
    if (k === 0) return null;
    return `$${k}K ${gap > 0 ? "behind" : "ahead"}`;
  })();

  const hasData = expected > 0 && annual > 0;

  // Positions on the bar, clamped so labels don't run off the right edge.
  const spentPct = hasData ? Math.min(100, (spent / annual) * 100) : 0;
  const expectedPct = hasData ? Math.min(100, (expected / annual) * 100) : 0;

  return (
    <div
      style={{
        background: C.card,
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        padding: "12px 14px 14px",
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 6,
          flexWrap: "wrap",
        }}
      >
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            CapEx · YTD Progress
          </div>
          <div style={{ fontSize: 11, color: C.dim, marginTop: 2 }}>
            {isOwner ? (demo ? "Owner share · " : "RRC share · ") : ""}
            {hasData
              ? `${fmt(spent)} spent of ${fmt(expected)} budgeted YTD · ~${fmt(annual)} annual est.`
              : "No CapEx budget recorded for this period."}
          </div>
        </div>
        {badge && gapLabel && (
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: badge.color,
              background: badge.bg,
              border: `1px solid ${badge.color}40`,
              padding: "3px 10px",
              borderRadius: 12,
              whiteSpace: "nowrap",
              fontFamily: "'DM Mono',monospace",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            {gapLabel}
          </span>
        )}
      </div>

      {!hasData ? null : (
        <>
          <div
            style={{
              position: "relative",
              height: 20,
              background: C.borderLight,
              borderRadius: 10,
              overflow: "visible",
              marginTop: 12,
              marginBottom: 22,
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                height: "100%",
                width: `${spentPct}%`,
                background: badge?.color || C.accent,
                borderRadius: 10,
                transition: "width 0.3s ease",
              }}
            />
            <div
              style={{
                position: "absolute",
                top: -4,
                bottom: -4,
                left: `${expectedPct}%`,
                width: 2,
                background: C.text,
                transform: "translateX(-1px)",
              }}
              title={`Expected by now: ${fmt(expected)}`}
            />
            <div
              style={{
                position: "absolute",
                top: "110%",
                left: `${expectedPct}%`,
                transform: "translateX(-50%)",
                fontSize: 10,
                color: C.sub,
                whiteSpace: "nowrap",
                fontFamily: "'DM Mono',monospace",
              }}
            >
              Expected · {fmt(expected)}
            </div>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              fontSize: 10,
              color: C.dim,
              fontFamily: "'DM Mono',monospace",
              letterSpacing: "0.04em",
              textTransform: "uppercase",
            }}
          >
            <span>$0</span>
            <span>
              Month {monthsElapsed} of 12 · Annual budget est. {fmt(annual)}
            </span>
          </div>
        </>
      )}
    </div>
  );
}
